import React, { useState, useRef, useEffect } from "react";
import {
  Modal,
  Box,
  Button,
  IconButton,
  Typography,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import "./Ocr-Scanner.scss";
import Compressor from "compressorjs";
import { Add, CameraswitchRounded } from "@mui/icons-material";
import Webcam from "react-webcam";
import { END_POINT } from "../../../utils/End-points";
import ApiClient from "../../../apis/Api-client";
import arrow_for_image_frame from "../../../assets/icons/arrow_for_image_frame.svg";
import { errorToster } from "../../../shared/toster/Toster";
import IconConstants from "../../../utils/IconConstants";
import { useTranslation } from "react-i18next";
import Carousel from "react-material-ui-carousel";
import ViewImageModal from "../../common-components/ViewImageModal";
import useNetworkStatus from "../../../hooks/useNetworkStatus";

const ReactOcrScanner = ({
  clearOcrData,
  handleOpen,
  openOcrScanner,
  getScannedImagePng,
  closeOcrScanner,
  getOcrData,
  defaultProofImage = {},
  setLoading,
}) => {
  const { t } = useTranslation("pages");
  const { api } = ApiClient();
  const fileInputRef = useRef(null);

  const [imageDataUrl, setImageDataUrl] = useState({
    proof_image_front: "",
    proof_image_back: "",
  });
  const [imagePng, setImagePng] = useState({
    proof_image_front: "",
    proof_image_back: "",
  });
  const isMob = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [currentState, setCurrentState] = useState("addBox");
  const [ImageName, setImageName] = useState("");
  const [isCameraAvailable, setisCameraAvailable] = useState(false);
  const [userMedia, setUserMedia] = useState(null);
  const [isConfirmDisabled, setIsConfirmDisabled] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");
  const fileUpload = useRef(null);
  const [tempImage, setTempImage] = useState({ url: "", file: "" });
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const { isOnline } = useNetworkStatus();

  const toggleFacingMode = () => {
    setFacingMode(facingMode === "user" ? "environment" : "user");
  };

  const webcamRef = useRef(null);

  const videoConstraints = {
    facingMode,
    width: { ideal: 1920 },
    height: { ideal: 1080 },
  };

  const handleUserMedia = (stream) => {
    setUserMedia(stream);
  };

  const navigationObj = {
    addBox: {
      nextScreen: "scanner",
    },
    scanner: {
      nextScreen: "viewImage",
      previousScreen: "addBox",
    },
    viewImage: {
      nextScreen: "addBox",
      previousScreen: "scanner",
    },
  };

  useEffect(() => {
    setOpen(openOcrScanner);
  }, [openOcrScanner]);

  useEffect(() => {
    if (clearOcrData) {
      setImageDataUrl({
        proof_image_front: "",
        proof_image_back: "",
      });
      setImagePng({
        proof_image_front: "",
        proof_image_back: "",
      });
    }
  }, [clearOcrData]);

  useEffect(() => {
    if (defaultProofImage && Object.entries(defaultProofImage)?.length > 0) {
      setImageDataUrl({
        proof_image_front: defaultProofImage?.length
          ? defaultProofImage[0]?.url
          : "",
        proof_image_back: defaultProofImage?.length
          ? defaultProofImage[1]?.url
          : "",
      });
      setIsConfirmDisabled(true);
    } else {
      setImageDataUrl({
        proof_image_front: "",
        proof_image_back: "",
      });
      setIsConfirmDisabled(false);
    }
  }, [defaultProofImage]);

  useEffect(() => {
    if (userMedia?.active) {
      setisCameraAvailable(true);
    } else {
      setisCameraAvailable(false);
    }
  }, [userMedia]);

  const handleConfirmation = async () => {
    if (
      !imagePng.proof_image_front ||
      !imagePng.proof_image_back ||
      !imageDataUrl.proof_image_front ||
      !imageDataUrl.proof_image_back
    ) {
      const missingPortion = !imagePng.proof_image_front
        ? "front_portion"
        : "back_portion";
      errorToster(
        t(`pages.booking_form.alert.${missingPortion}_of_the_image_is_missing`)
      );
      return;
    }
    setIsScanning(true);
    setLoading(true);
    const FormData = require("form-data");
    const formDataInstance = new FormData();
    formDataInstance.append("proof_image_front", imagePng.proof_image_front);
    formDataInstance.append("proof_image_back", imagePng.proof_image_back);

    try {
      if (isOnline) {
        let ocrData = await api.postFormData(
          END_POINT.OCR.SCAN_IMAGE,
          formDataInstance,
          "Error Getting Ocr Data"
        );
        if (ocrData?.status == 200) {
          getOcrData(ocrData?.data?.data);
        }
      }
      getScannedImagePng(imagePng);
    } catch (err) {
      // OCR request failed — notify the user; the finally closes the scanner.
      errorToster(
        t("pages.booking_form.alert.ocr_scan_failed", {
          defaultValue: "Failed to scan the document. Please try again.",
        })
      );
      closeOcrScanner();
    } finally {
      setIsScanning(false);
      setLoading(false);
      setOpen(false);
    }
  };

  const handleClose = () => {
    setIsConfirmDisabled(false);
    setOpen(false);
    setCurrentState("addBox");
    setImageDataUrl({
      proof_image_front: "",
      proof_image_back: "",
    });
    setImagePng({
      proof_image_front: "",
      proof_image_back: "",
    });
    closeOcrScanner();
  };

  const handleModalClose = () => {
    handleOpen();
    setisCameraAvailable(false);
  };

  const formatIntoPng = (file) => {
    return new Promise((resolve, reject) => {
      // const file = dataURLtoFile(dataURL, ImageName);
      if (!file) {
        reject(new Error("Failed to convert data URL to file."));
        return;
      }
      new Compressor(file, {
        quality: 0.8,
        maxWidth: 1600,
        maxHeight: 1600,
        mimeType: "image/jpeg",
        convertSize: 1000000,
        success: (compressedResult) => {
          resolve(compressedResult);
        },
        error: (error) => {
          reject(error);
        },
      });
    });
  };

  function dataURLtoFile(dataurl, filename) {
    if (!dataurl) {
      return null;
    }
    const arr = dataurl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  const clearCurrentImage = () => {
    handlePageNavigation("previousScreen");
    setImageDataUrl((prevState) => ({
      ...prevState,
      [ImageName]: "",
    }));
    setImagePng((prevState) => ({
      ...prevState,
      [ImageName]: "",
    }));
  };

  const handleCapture = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      const fileType = "image/png";
      const validImageTypes = [
        "image/jpeg",
        "image/png",
        "image/heif",
        "image/heic",
      ];
      if (!validImageTypes.includes(fileType)) {
        errorToster(t("pages.booking_form.toast.select_valid_image"));
        return;
      }

      // Convert base64 to blob to create object URL (similar to file input)
      const byteString = atob(imageSrc.split(",")[1]);
      const mimeString = imageSrc.split(",")[0].split(":")[1].split(";")[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: mimeString });
      const newUrl = URL.createObjectURL(blob);

      setTempImage({ url: newUrl, file: blob });
      handlePageNavigation("nextScreen");
    } else {
      console.error("Failed to capture image from webcam.");
    }
  };

  const handlePageNavigation = (page) => {
    const nextPageData = navigationObj?.[currentState]?.[page];

    setCurrentState(nextPageData);
  };

  const handleFileInputChange = (event) => {
    const imageInput = event.target.files[0];
    if (imageInput) {
      // const fileSize = imageInput.size; // Size in bytes
      // const maxSizeInBytes = 1 * 1024 * 1024; // 1 MB

      // if (fileSize > maxSizeInBytes) {
      //   errorToster("Selected file exceeds the maximum size");
      //   return;
      // }
      const fileType = imageInput.type;
      const validImageTypes = [
        "image/jpeg",
        "image/png",
        "image/heif",
        "image/heic",
      ];
      if (!validImageTypes.includes(fileType)) {
        errorToster(t("pages.booking_form.toast.select_valid_image"));
        return;
      }
      const newUrl = URL.createObjectURL(imageInput);
      setTempImage({ url: newUrl, file: imageInput });
      handlePageNavigation("nextScreen");
    }
  };

  const confirmImage = () => {
    formatIntoPng(tempImage?.file)
      .then((res) => {
        setImagePng((prevState) => ({
          ...prevState,
          [ImageName]: res,
        }));
      })
      .catch((error) => {
        console.error(error);
      });
    setImageDataUrl((prevState) => ({
      ...prevState,
      [ImageName]: tempImage?.url,
    }));
    handlePageNavigation("nextScreen");
  };

  const handleImgeModalClose = () => {
    setImages([]);
    setIsImageModalOpen(false);
  };

  return (
    <>
      <Modal open={open} onClose={handleModalClose}>
        <Box
          className="main-modal"
          sx={{
            width: { xs: "91.795vw", sm: "60%", md: "37%" },
            height: { xs: "111.282vw", sm: "auto", md: "auto" },
            padding: { sm: "5% 3%", md: "3%" },
            borderRadius: { xs: "20px", md: "36px" },
            position: "relative",
          }}
        >
          {/* Loader shown while the OCR scan request is in flight */}
          {isScanning && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                backgroundColor: "rgba(255, 255, 255, 0.85)",
                borderRadius: { xs: "20px", md: "36px" },
              }}
            >
              <CircularProgress color="primary" />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "3.5vw", sm: "1.8vw", md: "1vw" },
                  color: "#565656",
                }}
              >
                {t("pages.booking_form.scanning_document", {
                  defaultValue: "Scanning document...",
                })}
              </Typography>
            </Box>
          )}
          {isMobile && (
            <Box
              sx={{
                height: { xs: "10vh" },
                width: { xs: 1 },
              }}
            ></Box>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "15%",
              }}
            >
              {(currentState === "addBox" || !currentState) && (
                <Box
                  className="top-layout-frontbackbox"
                  sx={{
                    width: 1,
                    display: "flex",
                    marginX: isMobile ? "5%" : "",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10%",
                  }}
                >
                  <Box
                    sx={{
                      border: "1px solid #ECE5C7",
                      height: { xs: "39vw", sm: "160px" },
                      width: "50%",
                      borderRadius: "10px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        height: "80%",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {imageDataUrl?.proof_image_front &&
                      Object.entries(imageDataUrl?.proof_image_front)?.length >
                        0 ? (
                        <img
                          src={imageDataUrl.proof_image_front}
                          height="100%"
                          width="100%"
                          style={{
                            borderTopRightRadius: "10px",
                            borderTopLeftRadius: "10px",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setImages([
                              { url: imageDataUrl.proof_image_front },
                            ]);
                            setIsImageModalOpen(true);
                          }}
                        />
                      ) : (
                        <IconButton
                          onClick={() => {
                            handlePageNavigation("nextScreen");
                            setImageName("proof_image_front");
                          }}
                        >
                          <Add fontSize="large" />
                        </IconButton>
                      )}
                    </Box>
                    <Box
                      sx={{
                        height: "20%",
                        width: "100%",
                        backgroundColor: "#ECE5C7",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: { xs: "3.08vw", sm: "1.44vw", md: "0.83vw" },
                        fontWeight: 500,
                        borderBottomLeftRadius: "10px",
                        borderBottomRightRadius: "10px",
                      }}
                    >
                      {t("pages.booking_form.front")}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      border: "1px solid #ECE5C7",
                      height: { xs: "39vw", sm: "160px" },
                      width: "50%",
                      borderRadius: "10px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        height: "80%",
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {imageDataUrl?.proof_image_back &&
                      Object.entries(imageDataUrl.proof_image_back).length >
                        0 ? (
                        <img
                          src={imageDataUrl.proof_image_back}
                          height="100%"
                          width="100%"
                          style={{
                            borderTopRightRadius: "10px",
                            borderTopLeftRadius: "10px",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setImages([{ url: imageDataUrl.proof_image_back }]);
                            setIsImageModalOpen(true);
                          }}
                        />
                      ) : (
                        <IconButton
                          disabled={!imageDataUrl?.proof_image_front}
                          onClick={() => {
                            handlePageNavigation("nextScreen");
                            setImageName("proof_image_back");
                          }}
                        >
                          <Add fontSize="large" />
                        </IconButton>
                      )}
                    </Box>
                    <Box
                      sx={{
                        height: "20%",
                        width: "100%",
                        backgroundColor: "#ECE5C7",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: { xs: "3.08vw", sm: "1.44vw", md: "0.83vw" },
                        fontWeight: 500,
                        borderBottomLeftRadius: "10px",
                        borderBottomRightRadius: "10px",
                      }}
                    >
                      {t("pages.booking_form.back")}
                    </Box>
                  </Box>
                </Box>
              )}

              {currentState === "scanner" && !isMob && (
                <Box sx={{ width: 1 }}>
                  <Webcam
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    screenshotQuality={1}
                    forceScreenshotSourceSize
                    width={"90%"}
                    height={"280px"}
                    style={{
                      width: "90%",
                      height: "280px",
                      overflow: "hidden",
                      boxSizing: "border-box",
                      objectFit: "cover",
                    }}
                    mirrored={false}
                    onUserMedia={handleUserMedia}
                    videoConstraints={videoConstraints}
                  />
                  <Box
                    sx={{
                      width: { xs: "80%", sm: "70%" },
                      // height: { xs: "60vw", sm: "34vw", md: "20vw" },
                      height: "270px",
                      position: "absolute",
                      transform: "translate(-50%, -50%)",
                      top: { xs: "30%", sm: "33.5%", md: "33.5%" },
                      left: "50%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "space-between",
                      flexWrap: "wrap",
                    }}
                  >
                    <Box className="frame top-left">
                      <img
                        style={{
                          transform: "rotate(225deg) rotateX(180deg)",
                          filter: "invert(1)",
                        }}
                        src={arrow_for_image_frame}
                        width="23px"
                        height="29px"
                      />
                    </Box>
                    <Box className="frame top-right">
                      <img
                        style={{
                          transform: "rotate(315deg) rotateX(180deg)",
                          filter: "invert(1)",
                        }}
                        src={arrow_for_image_frame}
                        width="23px"
                        height="29px"
                      />
                    </Box>
                    <Box
                      className="frame bottom-left"
                      sx={{ position: "relative", top: "35%" }}
                    >
                      <img
                        style={{
                          transform: "rotate(135deg) rotateX(180deg)",
                          filter: "invert(1)",
                        }}
                        src={arrow_for_image_frame}
                        width="23px"
                        height="29px"
                      />
                    </Box>
                    <Box
                      className="frame bottom-right"
                      sx={{ position: "relative", top: "35%", left: "43%" }}
                    >
                      <img
                        style={{
                          transform: "rotate(45deg) rotateX(180deg)",
                          filter: "invert(1)",
                        }}
                        src={arrow_for_image_frame}
                        width="23px"
                        height="29px"
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 1,
                        fontSize: { xs: "3.08vw", sm: "1.68vw", md: "1.12vw" },
                        fontWeight: 500,
                        marginTop: "2%",
                      }}
                    >
                      Align your document within frame <br />
                      And tap to capture.
                    </Box>
                  </Box>
                </Box>
              )}

              {currentState === "viewImage" && (
                <Box sx={{ width: "90%", height: { xs: "60vw", sm: "280px" } }}>
                  <img
                    alt=""
                    src={tempImage?.url}
                    style={{
                      width: "100%",
                      height: "100%",
                      boxSizing: "border-box",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              )}
            </Box>

            <Box>
              {(currentState === "addBox" || !currentState) && (
                <>
                  {Object.entries(imageDataUrl?.proof_image_front ?? {})
                    ?.length > 0 ? (
                    ""
                  ) : (
                    <Typography
                      sx={{
                        fontSize: { xs: "3.08vw", sm: "1.68vw", md: "0.972vw" },
                        fontWeight: 500,
                        color: "#6B6B6B",
                        width: "55%",
                        margin: "0 auto",
                      }}
                    >
                      {t(
                        "pages.booking_form.start_uploading_the_front_side_of_the_document"
                      )}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      position: isMobile ? "fixed" : "",
                      bottom: isMobile ? "10%" : "",
                      display: "flex",
                      gap: "20px",
                      alignItems: "center",
                      justifyContent: "center",
                      marginTop: "30%",
                      left: isMobile ? "50%" : "",
                      transform: isMobile ? "translateX(-50%)" : "none",
                    }}
                  >
                    <Button
                      fullWidth
                      onClick={handleClose}
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "3.59vw", sm: "1.92vw", md: "1.12vw" },
                      }}
                      variant="outlined"
                    >
                      {t("pages.booking_form.cancel")}
                    </Button>
                    <Button
                      fullWidth
                      disabled={isConfirmDisabled}
                      onClick={handleConfirmation}
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "3.59vw", sm: "1.92vw", md: "1.12vw" },
                      }}
                      variant="contained"
                    >
                      {t("pages.booking_form.confirm")}
                    </Button>
                  </Box>
                </>
              )}

              {currentState === "scanner" && (
                <Box
                  sx={{
                    width: "90%",
                    margin: "0 auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginTop: "5%",
                  }}
                >
                  <Box sx={{ width: { xs: "30%", sm: "30%" } }}>
                    <input
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      type="file"
                      accept="image/*"
                      onChange={handleFileInputChange}
                    />
                    <IconButton onClick={() => fileInputRef.current.click()}>
                      <Box
                        sx={{
                          height: { xs: "30.51vw", sm: "3.2vw", md: "2.2vw" },
                          width: { xs: "30.51vw", sm: "4.2vw", md: "2.2vw" },
                        }}
                      >
                        <img
                          src={IconConstants.Gallery}
                          width="100%"
                          height="100%"
                        />
                      </Box>
                    </IconButton>
                  </Box>

                  <Box sx={{ width: { xs: "", sm: "30%" } }}>
                    <IconButton
                      // disabled={!isCameraAvailable}
                      onClick={() => {
                        isMob ? fileUpload?.current?.click() : handleCapture();
                      }}
                      sx={{ padding: 0 }}
                    >
                      <Box
                        sx={{
                          height: { xs: "30.51vw", sm: "8vw", md: "4.2vw" },
                          width: { xs: "30.51vw", sm: "8vw", md: "4.2vw" },
                        }}
                      >
                        <img
                          alt=""
                          src={IconConstants.Capture}
                          width="100%"
                          height="100%"
                        />
                      </Box>
                    </IconButton>
                    <input
                      accept="image/jpeg,image/png,image/heif,image/heic"
                      type="file"
                      capture={facingMode}
                      ref={fileUpload}
                      onChange={handleFileInputChange}
                      style={{ display: "none" }}
                    />
                  </Box>

                  <Box
                    sx={{
                      width: "20%",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {isMob && (
                      <IconButton
                        onClick={toggleFacingMode}
                        aria-label="switch camera"
                        size="small"
                        sx={{
                          border: "1px solid",
                          borderColor: "primary.main",
                          color: "primary.main",
                          backgroundColor: "rgba(247, 168, 110, 0.12)",
                          "&:hover": {
                            backgroundColor: "rgba(247, 168, 110, 0.22)",
                          },
                        }}
                      >
                        <CameraswitchRounded fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              )}

              {currentState === "viewImage" && (
                <Box
                  sx={{
                    width: "70%",
                    margin: "0 auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginTop: "18%",
                  }}
                >
                  <Box>
                    <IconButton
                      onClick={() => {
                        setisCameraAvailable(false);
                        clearCurrentImage();
                      }}
                    >
                      <Box
                        sx={{
                          position: isMobile ? "fixed" : "",
                          bottom: isMobile ? "10%" : "",
                          height: { xs: "7.69vw", sm: "3.95vw", md: "2.29vw" },
                          width: { xs: "7.69vw", sm: "3.95vw", md: "2.29vw" },
                        }}
                      >
                        <img
                          src={IconConstants.CloseCircle}
                          width="100%"
                          height="100%"
                        />
                      </Box>
                    </IconButton>
                  </Box>
                  <Box>
                    <IconButton
                      onClick={() => {
                        confirmImage();
                        setisCameraAvailable(false);
                      }}
                    >
                      <Box
                        sx={{
                          position: isMobile ? "fixed" : "",
                          bottom: isMobile ? "10%" : "",
                          height: { xs: "7.69vw", sm: "3.95vw", md: "2.29vw" },
                          width: { xs: "7.69vw", sm: "3.95vw", md: "2.29vw" },
                        }}
                      >
                        <img
                          src={IconConstants.TickCircle}
                          width="100%"
                          height="100%"
                        />
                      </Box>
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Modal>
      <ViewImageModal
        images={images}
        isOpen={isImageModalOpen}
        handleClose={handleImgeModalClose}
        isPrint={false}
      />
    </>
  );
};

export default ReactOcrScanner;
